// Phase 7-G スケジュール画像テンプレート（1080×1920、校舎 1 枚）
// HTML モック schedule_image_design_mockup.html を React 化したもの。
// インラインスタイルで書いているのは html-to-image との相性最大化のため。

import { useRef, useState, useLayoutEffect } from "react";

// 校舎情報（studio_id → 表示名 / ローマ字）。校舎追加時はここに追記。
const STUDIO_INFO = {
  1: { name: "浜　線　校", en: "HAMASEN" },
  2: { name: "白　山　校", en: "HAKUZAN" },
  3: { name: "帯　山　校", en: "OBIYAMA" },
  4: { name: "健　軍　校", en: "KENGUN" },
};

// 校舎ヘッダー帯の背景色パレット（studio_id 順に割当）。
// 先頭4色は元デザイン準拠（浜線=ピンク/白山=ターコイズ/帯山=薄黄/健軍=薄紫）。
// 5校目以降は (studio_id - 1) % palette.length で循環使用。
const STUDIO_PALETTE = [
  "#f8c8d0", // 1 ピンク
  "#b5e3dd", // 2 ターコイズ
  "#fde29b", // 3 薄黄
  "#d4c8f0", // 4 薄紫
  "#c8e6c9", // 5 薄緑
  "#ffd9b3", // 6 薄オレンジ
  "#b3d9ff", // 7 薄青
  "#f4c7e3", // 8 薄マゼンタ
  "#d9f0c0", // 9 黄緑
  "#ffd6d6", // 10 薄赤
  "#c0e4f0", // 11 水色
  "#e8d6a8", // 12 ベージュ
  "#cfe8cf", // 13 セージ
  "#f0d6bd", // 14 アプリコット
  "#dcd0f0", // 15 ラベンダー
  "#bfe8df", // 16 ミント
  "#f8e3a8", // 17 クリーム
  "#e6c8da", // 18 ローズ
  "#c8d6e8", // 19 スカイ
  "#e8e0c0", // 20 サンド
];

// studio_id から校舎テーマ（名前 / ローマ字 / 背景色）を引く
function getStudioTheme(studioId) {
  const id = Number(studioId) || 1;
  const info = STUDIO_INFO[id] || { name: `校舎${id}`, en: `STUDIO ${id}` };
  const bg = STUDIO_PALETTE[(id - 1) % STUDIO_PALETTE.length];
  return { ...info, bg };
}

// バッジ配色は元デザイン画像準拠（仕様 2.2.2 はオレンジ統一だが、
// クライアント提示済みデザインに合わせて状態ごとに色分け）。
// 一部代講 / 一部時間変更は画像に無いため、各「全○○」色を薄めた近似色を割当。
const BADGE_STYLE = {
  // 全○○
  fullClosed: { bg: "#ff6260", color: "#ffffff", label: "休講", twoLine: false },
  fullSubstitute: { bg: "#6ad8c8", color: "#1a1a1a", label: "代講", twoLine: false },
  fullReschedule: { bg: "#9bd86a", color: "#1a1a1a", label: "振替", twoLine: false },
  fullTimeChange: { bg: "#ffd24a", color: "#1a1a1a", label: "時間変更", twoLine: false },
  // 一部○○（2 行表示）
  partialClosed: { bg: "#f3a5b5", color: "#1a1a1a", label: ["一部", "休講"], twoLine: true },
  partialSubstitute: { bg: "#aee7dc", color: "#1a1a1a", label: ["一部", "代講"], twoLine: true },
  partialReschedule: { bg: "#b4dd86", color: "#1a1a1a", label: ["一部", "振替"], twoLine: true },
  partialTimeChange: { bg: "#ffe699", color: "#1a1a1a", label: ["一部", "時間変更"], twoLine: true },
};

const MONTH_NAMES_EN = [
  "JANUARY", "FEBRUARY", "MARCH", "APRIL", "MAY", "JUNE",
  "JULY", "AUGUST", "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER",
];
const DOW_JP = ["日", "月", "火", "水", "木", "金", "土"];

const LINE_DOTTED = "rgba(26,26,26,0.33)";

function getDaysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

function Badge({ kind, fitScale = 1 }) {
  const s = BADGE_STYLE[kind];
  if (!s) return null;
  return (
    <div
      style={{
        display: "inline-flex",
        flexDirection: s.twoLine ? "column" : "row",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        fontSize: 15 * fitScale,
        fontWeight: 700,
        padding: `${3 * fitScale}px 0`,
        borderRadius: 4,
        lineHeight: 1.1,
        width: 60,
        background: s.bg,
        color: s.color,
      }}
    >
      {s.twoLine ? (
        <>
          <span>{s.label[0]}</span>
          <span>{s.label[1]}</span>
        </>
      ) : (
        s.label
      )}
    </div>
  );
}

// line.remark（管理画面で入力した備考 = Schedule.note）は
// クラス行の下にインデントして描く。ここだけ whiteSpace:normal にして折り返す
// （同一行の末尾に続けると横幅 660px を超えて右端で切れるため）。
function LessonDetail({ lines, fitScale = 1 }) {
  if (!lines || lines.length === 0) return <div />;
  return (
    <div style={{ fontSize: 13 * fitScale, lineHeight: 1.2, whiteSpace: "nowrap" }}>
      {lines.map((line, i) => (
        <span key={i} style={{ display: "block" }}>
          <span style={{ display: "block" }}>
            {line.text}
            {line.note && (
              <span
                style={{
                  marginLeft: 4,
                  fontWeight: 700,
                  color: line.noteType === "red" ? "#d8483a" : "#38a060",
                }}
              >
                {line.note}
              </span>
            )}
          </span>
          {line.remark && (
            <span
              style={{
                display: "block",
                whiteSpace: "normal",
                paddingLeft: 16 * fitScale,
                opacity: 0.75,
              }}
            >
              ※{line.remark}
            </span>
          )}
        </span>
      ))}
    </div>
  );
}

function DateCell({ month, day, dow, fitScale = 1 }) {
  const dowChar = DOW_JP[dow];
  const isSat = dow === 6;
  const isSun = dow === 0;
  const dowColor = isSat ? "#2877d5" : isSun ? "#d8483a" : "#1a1a1a";
  const sub = { fontSize: 18 * fitScale, fontWeight: 500 };
  // 単純な inline 構造で「月」「日」を確実に描画
  return (
    <div
      style={{
        fontFamily: '"Hiragino Kaku Gothic ProN", "Noto Sans JP", sans-serif',
        fontSize: 22 * fitScale,
        fontWeight: 700,
        padding: `${5 * fitScale}px 0 ${5 * fitScale}px 14px`,
        color: "#1a1a1a",
        whiteSpace: "nowrap",
        display: "flex",
        alignItems: "center",
      }}
    >
      <span>{month}</span>
      <span style={{ ...sub, margin: "0 2px 0 1px" }}>月</span>
      <span>{day}</span>
      <span style={{ ...sub, margin: "0 4px 0 1px" }}>日</span>
      <span style={{ ...sub, color: dowColor }}>({dowChar})</span>
    </div>
  );
}

function Row({ month, day, dow, entry, fitScale = 1 }) {
  // flex: 1 0 auto = grow 1 / shrink 0 / basis auto(内容の自然高さ)。
  // 余り高さ(1920 - 全行の内容合計)を全行に均等配分するので、
  // 内容が多い行は自然に高く、空き行は最小高さ + 均等な余白になり、
  // 全日付が紙面いっぱいにバランスよく配置される。内容は潰れない。
  // fitScale は溢れ時のオートフィット縮小係数（1=縮小なし）。
  return (
    <div
      style={{
        flex: "1 0 auto",
        display: "grid",
        gridTemplateColumns: "210px 1fr",
        borderBottom: `1.5px dashed ${LINE_DOTTED}`,
        minHeight: 30 * fitScale,
        alignItems: "stretch",
      }}
    >
      <DateCell month={month} day={day} dow={dow} fitScale={fitScale} />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "68px 1fr",
          gap: 8,
          alignItems: "center",
          borderLeft: `2px dashed ${LINE_DOTTED}`,
          padding: `${3 * fitScale}px 0 ${3 * fitScale}px 10px`,
        }}
      >
        {entry ? <Badge kind={entry.badge} fitScale={fitScale} /> : <div />}
        {entry ? <LessonDetail lines={entry.lines} fitScale={fitScale} /> : <div />}
      </div>
    </div>
  );
}

function CircularMonthLabel({ monthIndex }) {
  const monthName = MONTH_NAMES_EN[monthIndex - 1] || "";
  const R = 70; // 半径（viewBox 160、中心 80）
  const circumference = 2 * Math.PI * R; // 円周長
  // 「JUNE · 」の単位を円周がほぼ埋まる回数だけ繰り返し、
  // textLength=円周長 + lengthAdjust="spacing" で均等に一周させる（月名長に依存しない）
  const unit = `${monthName} · `;
  const reps = Math.max(3, Math.round(circumference / (unit.length * 8)));
  const text = unit.repeat(reps);
  // 同一ページに複数テンプレートが出ても id 衝突しないよう月でユニーク化
  const pathId = `month-arc-${monthIndex}`;
  return (
    <svg
      viewBox="0 0 160 160"
      xmlns="http://www.w3.org/2000/svg"
      style={{ position: "absolute", inset: 0 }}
    >
      <defs>
        <path
          id={pathId}
          d={`M 80,80 m -${R},0 a ${R},${R} 0 1,1 ${2 * R},0 a ${R},${R} 0 1,1 -${2 * R},0`}
        />
      </defs>
      <text
        fontFamily="Inter, sans-serif"
        fontSize="12"
        fontWeight="700"
        fill="#1a1a1a"
      >
        <textPath
          href={`#${pathId}`}
          startOffset="0"
          textLength={circumference}
          lengthAdjust="spacing"
        >
          {text}
        </textPath>
      </text>
    </svg>
  );
}

// メインテンプレート
// props:
//   year, month (1-12): 表示する年月
//   studioId: 校舎ID（1=浜線/2=白山/3=帯山/4=健軍…）色は id 順にパレット割当
//   entries: { [day:number]: { badge, lines: [{text, note?, noteType?}] } }
export default function ScheduleImageTemplate({
  year = 2026,
  month = 4,
  studioId = 1,
  entries = {},
  onFit,
}) {
  const theme = getStudioTheme(studioId);
  const daysInMonth = getDaysInMonth(year, month);

  // オートフィット縮小：内容合計が利用可能高さを超えたら fitScale を下げて必ず1枚に収める。
  // 測定は fitScale=1（縮小なし）のときの自然高さで行い、振動を防ぐ。
  const tableBodyRef = useRef(null);
  const [fitScale, setFitScale] = useState(1);

  // 年月・校舎・データが変わったら一旦 fitScale=1 に戻して再測定の起点にする
  useLayoutEffect(() => {
    setFitScale(1);
  }, [year, month, studioId, entries]);

  useLayoutEffect(() => {
    if (fitScale !== 1) return; // 縮小適用後は測らない（収束のため）
    const el = tableBodyRef.current;
    if (!el) return;
    const natural = el.scrollHeight; // 内容の自然な合計高さ
    const avail = el.clientHeight; // 行コンテナが使える高さ
    if (natural > avail + 1) {
      // 0.55 を下限に、収まるよう比例縮小
      const wanted = avail / natural;
      setFitScale(Math.max(0.55, wanted));
      // 下限にクランプされた＝縮めても収まらず、下部の日付が欠落する
      if (onFit) onFit({ fitScale: Math.max(0.55, wanted), overflow: wanted < 0.55 });
    } else if (onFit) {
      onFit({ fitScale: 1, overflow: false });
    }
    // onFit は毎レンダー変わる可能性があるため依存に入れない（測定の再入を防ぐ）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitScale, year, month, studioId, entries]);

  const rows = [];
  for (let day = 1; day <= daysInMonth; day++) {
    const dow = new Date(year, month - 1, day).getDay();
    rows.push(
      <Row
        key={day}
        month={month}
        day={day}
        dow={dow}
        entry={entries[day]}
        fitScale={fitScale}
      />,
    );
  }

  return (
    <div
      className="schedule-image-canvas"
      style={{
        width: 1080,
        height: 1920,
        background: "#ffffff",
        color: "#1a1a1a",
        position: "relative",
        overflow: "hidden",
        fontFamily: '"Hiragino Kaku Gothic ProN", "Noto Sans JP", sans-serif',
        fontWeight: 500,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* admin の global SCSS で span/p/a/li/h* が color:#fff 指定されているため、
          このキャンバス内では color を継承させて親 div の色を効かせる。
          inline style で色指定された要素はそのまま勝つ。 */}
      <style>{`
        .schedule-image-canvas span,
        .schedule-image-canvas p,
        .schedule-image-canvas a,
        .schedule-image-canvas li,
        .schedule-image-canvas h1,
        .schedule-image-canvas h2,
        .schedule-image-canvas h3,
        .schedule-image-canvas h4,
        .schedule-image-canvas h5,
        .schedule-image-canvas i {
          color: inherit;
          font-family: inherit;
        }
      `}</style>
      {/* 透かしロゴ（◐ マークを縦向きで背景に大きく、元デザイン準拠の薄グレー）
          二重丸の内円右端が画像右端に合う位置（内円右端=幅78%、right=-139 で算出） */}
      <div
        style={{
          position: "absolute",
          right: -139,
          bottom: 150,
          height: 980,
          opacity: 0.1,
          pointerEvents: "none",
          zIndex: 0,
        }}
      >
        <img
          src="/images/logo1-mark-dark-ccw.png"
          alt=""
          style={{ height: "100%", width: "auto", display: "block" }}
        />
      </div>

      {/* ヘッダー（左列は月装飾と対称のスペーサー、タイトル中央、右に月装飾） */}
      <div
        style={{
          position: "relative",
          flexShrink: 0,
          padding: "20px 60px 0",
          display: "grid",
          gridTemplateColumns: "130px 1fr 130px",
          gap: 16,
          alignItems: "start",
        }}
      >
        <div />
        <div style={{ textAlign: "center", marginTop: 4 }}>
          <div
            style={{
              fontSize: 15,
              letterSpacing: "0.5em",
              opacity: 0.7,
              fontWeight: 400,
            }}
          >
            アイヴィーダンススタジオ
          </div>
          <div
            style={{
              fontFamily: '"Inter", "Noto Sans JP", sans-serif',
              fontSize: 52,
              fontWeight: 900,
              letterSpacing: "-0.01em",
              lineHeight: 1,
              marginTop: 2,
            }}
          >
            Lesson Schedule
          </div>
          <div
            style={{
              fontSize: 24,
              fontWeight: 700,
              letterSpacing: "0.3em",
              marginTop: 6,
            }}
          >
            {theme.name}
          </div>
          <div
            style={{
              fontSize: 12,
              letterSpacing: "0.4em",
              opacity: 0.6,
              fontWeight: 400,
              marginTop: 2,
            }}
          >
            {theme.en}
          </div>
        </div>
        <div
          style={{
            position: "relative",
            width: 130,
            height: 130,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularMonthLabel monthIndex={month} />
          <div
            style={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 900,
              fontSize: 80,
              lineHeight: 1,
              position: "relative",
              zIndex: 2,
            }}
          >
            {month}
          </div>
          <div
            style={{
              fontFamily: '"Inter", sans-serif',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.15em",
              lineHeight: 1,
              marginTop: 4,
              position: "relative",
              zIndex: 2,
            }}
          >
            {year}
          </div>
        </div>
      </div>

      {/* Ivy キャプション */}
      <div
        style={{
          flexShrink: 0,
          padding: "2px 60px 0",
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 13,
          letterSpacing: "0.25em",
          fontWeight: 400,
          opacity: 0.7,
        }}
      >
        Ivy Dance Studio
        <img
          src="/images/logo1-mark-dark.png"
          alt=""
          style={{ height: 15, width: "auto" }}
        />
      </div>

      {/* テーブル（残り高さいっぱいに伸ばし、行を均等配分）
          下マージンはフッター(◐ + ©表記)が罫線より下に収まる余白を確保 */}
      <div
        style={{
          flex: "1 1 auto",
          margin: "8px 60px 78px",
          borderTop: "3px solid #1a1a1a",
          borderBottom: "3px solid #1a1a1a",
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <div
          style={{
            flexShrink: 0,
            display: "grid",
            gridTemplateColumns: "210px 1fr",
            alignItems: "end",
            borderBottom: `2px dashed ${LINE_DOTTED}`,
          }}
        >
          <div />
          <div style={{ textAlign: "center", padding: "4px 0 3px" }}>
            <div
              style={{
                fontFamily: '"Inter", sans-serif',
                fontSize: 12,
                letterSpacing: "0.5em",
                opacity: 0.65,
                marginBottom: 2,
              }}
            >
              {theme.en}
            </div>
            <div
              style={{
                fontSize: 28,
                fontWeight: 700,
                letterSpacing: "0.2em",
                padding: "5px 0",
                background: theme.bg,
              }}
            >
              {theme.name}
            </div>
          </div>
        </div>
        {/* 行領域：残り高さを埋めつつ、内容が溢れたら fitScale で縮小（測定対象） */}
        <div
          ref={tableBodyRef}
          style={{
            flex: "1 1 auto",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {rows}
        </div>
      </div>

      {/* フッター（◐ マーク上・©表記下の縦並び中央、テーブル罫線より下に配置） */}
      <div
        style={{
          position: "absolute",
          bottom: 14,
          right: 56,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
          opacity: 0.7,
          zIndex: 2,
        }}
      >
        <img
          src="/images/logo1-mark-dark.png"
          alt=""
          style={{ width: 38, height: "auto", display: "block" }}
        />
        <span style={{ fontSize: 10, letterSpacing: "0.05em" }}>
          ©Ivy Dance Studio
        </span>
      </div>
    </div>
  );
}

// サンプルデータ（5 種バッジ全部入り、密度高め）
// 描画密度と「30 日が 1080×1920 に収まるか」のストレステスト用。
export const SAMPLE_ENTRIES = {
  2: { badge: "fullClosed", lines: [] },
  3: {
    badge: "fullTimeChange",
    lines: [
      { text: "【18:00 - 19:00 ERIKA HIPHOP 初級】", note: "→ 17:00-18:00", noteType: "green" },
    ],
  },
  6: {
    badge: "fullSubstitute",
    lines: [
      { text: "【19:30-20:30 AMI JAZZFUNK 初級】", note: "※AKARI代講", noteType: "green" },
      { text: "【20:30-21:30 AMI JAZZFUNK 中級】", note: "※AKARI代講", noteType: "green" },
    ],
  },
  9: {
    badge: "partialReschedule",
    lines: [
      { text: "【18:00 - 19:00 MIRI HIPHOP 初級】", note: "3/19(木)分の振替", noteType: "green" },
    ],
  },
  10: {
    badge: "partialClosed",
    lines: [
      { text: "【19:00 - 20:00 JUNA R&B 初級】", note: "⚠休講", noteType: "red" },
      { text: "【20:00 - 21:00 JUNA HIPHOP 中級】通常営業" },
    ],
  },
  13: {
    badge: "fullSubstitute",
    lines: [
      { text: "【19:30-20:30 AMI JAZZFUNK 初級】", note: "※AKARI代講", noteType: "green" },
      { text: "【20:30-21:30 AMI JAZZFUNK 中級】", note: "※AKARI代講", noteType: "green" },
    ],
  },
  15: { badge: "fullClosed", lines: [] },
  16: {
    badge: "partialClosed",
    lines: [
      { text: "【18:00 - 19:00 MIRI HIPHOP 初級】", note: "⚠休講", noteType: "red" },
      { text: "【19:00 - 20:00 CHANRIO K-POP 初級】通常営業" },
      { text: "【20:00 - 21:00 SHUN POP 初級】通常営業" },
      { text: "【21:00 - 22:00 SHUN LOCK 初級】通常営業" },
    ],
  },
  17: {
    badge: "fullTimeChange",
    lines: [
      { text: "【19:00 - 20:00 JUNA R&B 初級】", note: "→ 19:30-20:30", noteType: "green" },
      { text: "【20:00 - 21:00 JUNA HIPHOP 中級】", note: "→ 20:30-21:30", noteType: "green" },
    ],
  },
  20: {
    badge: "fullSubstitute",
    lines: [
      { text: "【19:30-20:30 AMI JAZZFUNK 初級】", note: "※AKARI代講", noteType: "green" },
      { text: "【20:30-21:30 AMI JAZZFUNK 中級】", note: "※AKARI代講", noteType: "green" },
    ],
  },
  22: {
    badge: "partialReschedule",
    lines: [
      { text: "【19:00 - 20:00 CHANRIO K-POP 初級】", note: "4/15(水)分の振替", noteType: "green" },
      { text: "【20:00 - 21:00 SHUN POP 初級】", note: "4/15(水)分の振替", noteType: "green" },
    ],
  },
  24: {
    badge: "partialClosed",
    lines: [
      { text: "【19:00 - 20:00 ERIKA GIRLS 入門】", note: "⚠休講", noteType: "red" },
      { text: "【20:00 - 21:00 ERIKA GIRLS 中級】通常営業" },
    ],
  },
  27: {
    badge: "fullSubstitute",
    lines: [
      { text: "【19:30-20:30 AMI JAZZFUNK 初級】", note: "※AKARI代講", noteType: "green" },
      { text: "【20:30-21:30 AMI JAZZFUNK 中級】", note: "※AKARI代講", noteType: "green" },
    ],
  },
  29: { badge: "fullClosed", lines: [] },
  30: {
    badge: "partialReschedule",
    lines: [
      { text: "【19:00 - 20:00 CHANRIO K-POP 初級】", note: "3/26(木)分の振替", noteType: "green" },
      { text: "【18:00 - 19:00 MIRI HIPHOP 初級】", note: "4/16(木)分の振替", noteType: "green" },
      { text: "【20:00 - 21:00 SHUN POP 初級】", note: "⚠休講", noteType: "red" },
      { text: "【21:00 - 22:00 SHUN LOCK 初級】", note: "⚠休講", noteType: "red" },
    ],
  },
};

// 負荷確認用：全日に 4 行ずつ詰めた極端データ（オートフィット縮小の検証用）
export function buildStressEntries(daysInMonth = 31) {
  const e = {};
  for (let d = 1; d <= daysInMonth; d++) {
    e[d] = {
      badge: "partialClosed",
      lines: [
        { text: "【18:00 - 19:00 MIRI HIPHOP 初級】", note: "⚠休講", noteType: "red" },
        { text: "【19:00 - 20:00 CHANRIO K-POP 初級】通常営業" },
        { text: "【20:00 - 21:00 SHUN POP 初級】通常営業" },
        { text: "【21:00 - 22:00 SHUN LOCK 初級】通常営業" },
      ],
    };
  }
  return e;
}
